import './index.scss';

import { Component } from 'react';
import { BraxButton, BraxForm, BraxLoading, BraxModal, BraxModals, BraxModalsType, BraxPhoneField, BraxSelectField, BraxSelectOption, BraxToasts, BraxToastsType } from 'design-system';
import { Asserts } from 'yup';

import paymentInterval, { PaymentInterval } from '../../../variables/payment-interval';
import t from '../../../utils/i18n';
import handleError from '../../../utils/errors';
import { withContexts } from '../../../utils/with-context';
import Plan from '../../../view-model/plans/Plan';
import User from '../../../view-model/users/User';
import upgradeSchema, { UpgradeValue } from '../../../schemas/plans/upgrade';
import plansService from '../../../services/plans';
import UserContext from '../../../contexts/User';
import paymentsService from '../../../services/payments';

export type UpgradeUserPlanProps = {
    modals: BraxModalsType,
    toasts: BraxToastsType,
    user: User,
    open?: boolean,
    plan?: number,
    coupon?: string,
    interval?: PaymentInterval,
    onClose?: () => void
};

type UpgradeUserPlanState = {
    userPlan: Plan,
    plans: BraxSelectOption<Plan>[],
    initialValues: Partial<Asserts<typeof upgradeSchema>>,
    loading: boolean,
    saving: boolean
};

export default withContexts({
    modals: BraxModals,
    toasts: BraxToasts,
    user: UserContext
}, class UpgradeUserPlan extends Component<UpgradeUserPlanProps, UpgradeUserPlanState> {

    state: UpgradeUserPlanState = {
        userPlan: this.props.user.plan,
        plans: [],
        initialValues: {},
        loading: true,
        saving: false
    };

    schema = upgradeSchema;

    paymentIntervalOptions = [...paymentInterval];

    async componentDidMount() {
        try {
            const plans = (await plansService.getPlans()).result;

            const userPlan = this.props.user.plan.isFree && this.props.user.subscription?.isActive ? plans.find(plan => plan.id === this.props.user.subscription!.idPlan) || this.props.user.plan : this.props.user.plan;

            const filteredPlans = plans.filter(this.props.user.subscription?.type === 'yearly' ? plan => plan.id > userPlan.id : plan => plan.id >= userPlan.id);

            this.setState({
                userPlan: userPlan,
                plans: filteredPlans.map(plan => ({ value: plan, title: plan.title })),
                initialValues: {
                    actualPlan: userPlan.id,
                    actualInterval: this.props.user.subscription?.type,
                    plan: (this.props.plan !== undefined && filteredPlans.find(plan => plan.id === this.props.plan!)) || filteredPlans[0],
                    interval: this.props.interval,
                    coupon: this.props.coupon,
                    phone: this.props.user.phoneNumber
                } as any,
                loading: false
            });
        } catch (error) {
            this.props.toasts.create({ type: 'destructive', ...handleError(error, 'Falha ao carregar os dados dos planos') });

            this.setState({ loading: false });
        }
    }

    async send({ plan, ...values }: UpgradeValue) {
        this.setState({ saving: true });

        try {
            await paymentsService.requestUpgrade({ user: this.props.user.id, plan: plan.id, ...values });

            this.props.toasts.create({
                type: 'success',
                title: t('Solicitação enviada!'),
                description: t('Entraremos em contato via Whatsapp em breve.')
            });

            this.props.onClose?.();
        } catch (error) {
            this.props.toasts.create({ type: 'destructive', ...handleError(error) });

            this.setState({ saving: false });
        }
    }

    render() {
        if (this.state.loading)
            return (<BraxLoading />);

        return (
            <BraxModal
                open={this.props.open}
                className="upgrade-user-plan"
                size="md"
                onClose={this.props.onClose}
            >
                <header>
                    <h2>{ t('Solicitação de upgrade') }</h2>

                    <p>{ t('Solicite o upgrade de plano aqui.') }</p>
                </header>

                <BraxForm
                    schema={this.schema}
                    values={this.state.initialValues}
                    onSubmit={values => this.send(values as UpgradeValue)}
                    validateOnMount
                >
                    <p>{ t('Informe o plano e condição que deseja fazer o upgrade:') }</p>

                    <BraxSelectField
                        name="plan"
                        label={t('Plano selecionado')}
                        options={this.state.plans}
                    />

                    <BraxSelectField
                        label={t('Condição de pagamento')}
                        name="interval"
                        options={this.paymentIntervalOptions}
                    />

                    <p>{ t('Entraremos em contato através do Whatsapp:') }</p>

                    <BraxPhoneField
                        name="phone"
                        label={t('Whatsapp')}
                        placeholder={t('ex. +55 12 12345 1234')}
                        whatsapp={false}
                    />

                    <div className="actions">
                        <BraxButton variant="secondary" size="md" disabled={this.state.saving} onClick={this.props.onClose}>{ t('Cancelar') }</BraxButton>

                        <BraxButton type="submit" size="md" disabled={this.state.saving}>{ t(this.state.saving ? 'Enviando...' : 'Enviar Solicitação') }</BraxButton>
                    </div>
                </BraxForm>
            </BraxModal>
        );
    }

});
